import React from "react";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  Divider,
  Avatar,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { type UserRole, type UpdateUserPayload } from "../utils/api";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  deleteUserThunk,
  fetchUserByIdThunk,
  updateUserThunk,
} from "../features/users/usersSlice";
import { uploadFilesToCloudinary } from "../utils/cloudinary";

const defaultFormState = {
  name: "",
  email: "",
  phoneNumber: "",
  address: "",
  imageUrl: "",
  role: "member" as UserRole,
  password: "",
};

const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    data: user,
    status: detailStatus,
    error: detailError,
  } = useAppSelector((state) => state.users.detail);
  const [formValues, setFormValues] =
    React.useState<typeof defaultFormState>(defaultFormState);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const loadUser = React.useCallback(() => {
    if (!id) {
      return Promise.resolve();
    }
    return dispatch(fetchUserByIdThunk(id));
  }, [dispatch, id]);

  React.useEffect(() => {
    if (id) {
      void loadUser();
    }
  }, [id, loadUser]);

  React.useEffect(() => {
    if (user) {
      setFormValues({
        name: user.name ?? "",
        email: user.email ?? "",
        phoneNumber: user.phoneNumber ?? "",
        address: user.address ?? "",
        imageUrl: user.imageUrl ?? "",
        role: user.role,
        password: "",
      });
    }
  }, [user]);

  const isLoading =
    !user && (detailStatus === "idle" || detailStatus === "loading");

  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: name === "role" ? (value as UserRole) : value,
    }));
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!event.target.files?.length) {
      return;
    }
    setUploadingImage(true);
    try {
      const [url] = await uploadFilesToCloudinary(
        Array.from(event.target.files),
      );
      if (url) {
        setFormValues((prev) => ({ ...prev, imageUrl: url }));
      }
    } catch {
      await Swal.fire({
        title: "Upload failed",
        text: "Unable to upload image right now. Please retry.",
        icon: "error",
        confirmButtonColor: "#0f62fe",
      });
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!id) {
      return;
    }
    setSaving(true);
    try {
      const payload: UpdateUserPayload = {
        name: formValues.name,
        email: formValues.email,
        phoneNumber: formValues.phoneNumber || undefined,
        address: formValues.address || undefined,
        imageUrl: formValues.imageUrl || undefined,
        role: formValues.role,
      };
      if (formValues.password.trim()) {
        payload.password = formValues.password;
      }

      await dispatch(updateUserThunk({ userId: id, payload })).unwrap();
      setFormValues((prev) => ({ ...prev, password: "" }));
    } catch {
      // Errors are surfaced through toast notifications
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) {
      return;
    }
    const result = await Swal.fire({
      title: "Delete this user?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3a6ff7",
      confirmButtonText: "Delete user",
    });
    if (!result.isConfirmed) {
      return;
    }
    setDeleting(true);
    try {
      await dispatch(deleteUserThunk(id)).unwrap();
      await Swal.fire({
        title: "User removed",
        text: "The user has been deleted successfully.",
        icon: "success",
        confirmButtonColor: "#0f62fe",
      });
      navigate("/user");
    } catch {
      await Swal.fire({
        title: "Unable to delete",
        text: "Please try again in a moment.",
        icon: "error",
        confirmButtonColor: "#0f62fe",
      });
    } finally {
      setDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Paper
        sx={{
          p: 6,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  if (!user) {
    return (
      <Paper
        sx={{
          p: 4,
          borderRadius: 2,
          border: "1px solid rgba(15, 41, 74, 0.08)",
        }}
      >
        <Typography variant="h6" mb={1}>
          User not found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {detailError ||
            "We couldn’t locate that record. Return to the directory and try again."}
        </Typography>
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          onClick={() => navigate("/user")}
        >
          Back to directory
        </Button>
      </Paper>
    );
  }

  const joinedAt = dayjs(user.createdAt).format("DD-MM-YY HH-mm A");

  const formSurfaceStyles = {
    borderRadius: "4px",
    border: "1px solid rgba(16, 53, 94, 0.08)",
    boxShadow: "0 25px 60px rgba(13, 37, 72, 0.08)",
    backgroundColor: "#ffffff",
  } as const;

  const heroSurfaceStyles = {
    borderRadius: "4px",
    background: "#fff)",
    border: "1px solid rgba(19, 71, 124, 0.12)",
    px: { xs: 3, md: 4 },
    py: { xs: 3, md: 4 },
    boxShadow: "0 25px 60px rgba(13, 37, 72, 0.08)",
  } as const;

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Paper sx={heroSurfaceStyles}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          gap={3}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={formValues.imageUrl || user.imageUrl}
              sx={{
                width: 70,
                height: 70,
                fontSize: 30,
                bgcolor: "#d8ecff",
                color: "#0c3b66",
              }}
            >
              {user.name
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((word) => word[0]?.toUpperCase())
                .join("")}
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={600} color="#0b2747">
                {user.name}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Joined {joinedAt}
              </Typography>
              <Button
                component="label"
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
                disabled={uploadingImage}
              >
                {uploadingImage ? "Uploading..." : "Upload new photo"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                />
              </Button>
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={1.5}
            width={{ xs: "100%", md: "auto" }}
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
          >
            <Button variant="outlined" onClick={() => navigate("/user")}>
              Back to directory
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete user"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {detailError && <Alert severity="error">{detailError}</Alert>}

      <Paper
        component="form"
        onSubmit={handleSave}
        sx={{ ...formSurfaceStyles, p: { xs: 3, md: 4 } }}
      >
        <Typography variant="h6" fontWeight={600} mb={2}>
          Profile details
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Stack spacing={3}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Full name"
              name="name"
              fullWidth
              value={formValues.name}
              onChange={handleFieldChange}
              required
            />
            <TextField
              label="Role"
              name="role"
              select
              fullWidth
              value={formValues.role}
              onChange={handleFieldChange}
            >
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="member">Member</MenuItem>
            </TextField>
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Email"
              name="email"
              type="email"
              fullWidth
              value={formValues.email}
              onChange={handleFieldChange}
              required
            />
            <TextField
              label="Phone number"
              name="phoneNumber"
              fullWidth
              value={formValues.phoneNumber}
              onChange={handleFieldChange}
            />
          </Stack>
          <TextField
            label="Address"
            name="address"
            fullWidth
            value={formValues.address}
            onChange={handleFieldChange}
          />
          <TextField
            label="Reset password"
            name="password"
            type="password"
            helperText="Leave blank to keep the current password."
            fullWidth
            value={formValues.password}
            onChange={handleFieldChange}
          />
        </Stack>
        <Divider sx={{ my: 3 }} />
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={() => void loadUser()}
            disabled={saving}
          >
            Reset changes
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserDetailPage;
